import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isTalentAbility, looksLikeTalentToken, talentHoverLabel } from '@dota/shared';
import { api, type Ability } from '../lib/api.ts';

/**
 * Ability imagery, for skill-build rows.
 *
 * Matches store bare ability ids, so a lookup is needed to render anything but
 * a number. Loaded once for the app and served from Valve's CDN, the same way
 * items are. `/constants/abilities` has no id; the API merges it with
 * `/constants/ability_ids`.
 *
 * Talent points (`special_bonus_*`) have no real ability art. OpenDota's
 * `dname` is often an unsubstituted token (`+{s:bonus}`), and a 404 image
 * would dump that token as `alt` into the row. Those slots are a talent-tree
 * tile of the same size, with the real name only on hover.
 */

const ABILITY_IMAGE_BASE =
  'https://cdn.cloudflare.steamstatic.com/apps/dota2/images/dota_react/abilities';

interface AbilityLookup {
  name: (abilityId: number) => string;
  slug: (abilityId: number) => string | null;
}

const AbilityContext = createContext<AbilityLookup | null>(null);

export function AbilityProvider({ children }: { children: ReactNode }) {
  const { data } = useQuery({
    queryKey: ['abilities'],
    queryFn: api.abilities,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
  });

  const value = useMemo<AbilityLookup>(() => {
    const byId = new Map<number, Ability>((data?.abilities ?? []).map((a) => [a.id, a]));
    return {
      name: (abilityId) => byId.get(abilityId)?.name ?? `Ability ${abilityId}`,
      slug: (abilityId) => byId.get(abilityId)?.slug ?? null,
    };
  }, [data]);

  return <AbilityContext.Provider value={value}>{children}</AbilityContext.Provider>;
}

export function useAbilities(): AbilityLookup {
  const context = useContext(AbilityContext);
  if (context) return context;
  return { name: (id) => `Ability ${id}`, slug: () => null };
}

const SIZES = { sm: 22, md: 28 } as const;

/** The in-game talent-tree glyph, sized to match a skill icon. */
function TalentTreeIcon({
  label,
  px,
}: {
  label: string;
  px: number;
}) {
  return (
    <span
      title={label}
      role="img"
      aria-label={label}
      className="flex shrink-0 items-center justify-center overflow-hidden rounded-[2px] bg-surface-3"
      style={{ width: px, height: px }}
    >
      <svg
        width={px - 2}
        height={px - 2}
        viewBox="0 0 20 20"
        aria-hidden="true"
        className="text-warn"
      >
        <circle cx="10" cy="3.2" r="1.55" fill="currentColor" />
        <circle cx="5.2" cy="8.2" r="1.45" fill="currentColor" />
        <circle cx="14.8" cy="8.2" r="1.45" fill="currentColor" />
        <circle cx="5.2" cy="13.1" r="1.45" fill="currentColor" />
        <circle cx="14.8" cy="13.1" r="1.45" fill="currentColor" />
        <circle cx="5.2" cy="17.4" r="1.35" fill="currentColor" />
        <circle cx="14.8" cy="17.4" r="1.35" fill="currentColor" />
        <path
          d="M10 4.6v2.2M10 6.8 5.6 8.1M10 6.8l4.4 1.3M5.2 9.7v2M14.8 9.7v2M5.2 14.6v1.3M14.8 14.6v1.3"
          stroke="currentColor"
          strokeWidth="1.15"
          fill="none"
        />
      </svg>
    </span>
  );
}

/**
 * One point in a skill build.
 *
 * `generic_hidden` is a real Valve slug for unused slots — we keep the box
 * so the upgrade order stays aligned, and skip the broken image.
 */
export function AbilityIcon({
  abilityId,
  size = 'sm',
}: {
  abilityId: number;
  size?: keyof typeof SIZES;
}) {
  const lookup = useAbilities();
  const slug = lookup.slug(abilityId);
  const rawName = lookup.name(abilityId);
  const px = SIZES[size];
  const [failed, setFailed] = useState(false);

  const talent = isTalentAbility(slug) || looksLikeTalentToken(rawName);
  if (talent) {
    const label = talentHoverLabel(slug, rawName);
    return <TalentTreeIcon label={label} px={px} />;
  }

  const showImage = slug != null && slug.length > 0 && slug !== 'generic_hidden' && !failed;
  /*
   * Never put a localisation token in `alt`. A 404 ability image used to dump
   * that string into the skill-build row (Vengeful Spirit was the worst).
   */
  const alt = looksLikeTalentToken(rawName) ? '' : rawName;

  return (
    <span
      title={rawName}
      className="block shrink-0 overflow-hidden rounded-[2px] bg-surface-3"
      style={{ width: px, height: px }}
    >
      {showImage && (
        <img
          src={`${ABILITY_IMAGE_BASE}/${slug}.png`}
          alt={alt}
          width={px}
          height={px}
          loading="lazy"
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      )}
    </span>
  );
}

/** Skill points in the order they were spent. One even row of equal tiles. */
export function AbilityBuild({ abilityIds }: { abilityIds: readonly number[] }) {
  if (abilityIds.length === 0) return null;
  return (
    <div className="flex flex-nowrap gap-0.5">
      {abilityIds.map((id, index) => (
        <AbilityIcon key={`${id}-${index}`} abilityId={id} />
      ))}
    </div>
  );
}
